import {
    getAttributeQty,
    getAttributeValue,
    getBoxVentCount,
    getMeasurementLf,
    getTurbineVentCount,
    isRoofVentilationType,
    RIDGE_VENT_VENTILATION_TYPE,
} from "../../../../../utils/formulaScope";
import {
    buildLinesFromFurnaceInspection,
    isMultiLineSelection,
    MULTI_LINE_MATERIAL_CATEGORIES,
} from "./materialLineHelpers";
import {
    calculateQuantity,
    findManufacturerProductId,
    findSubcategory,
    hasColorOptions,
} from "./productHelpers";

export interface InspectionMaterialPrefillRule {
    /** inspection_attributes.AttributeName */
    attributeName: string;
    /** Fixed material category (use resolveCategory when value picks the category) */
    category?: string;
    resolveCategory?: (attributeValue: string) => string | null;
    /** Map inspection AttributeValue → estimate product Name. Return null to skip. */
    resolveProductValue?: (attributeValue: string, category: string) => string | null;
}

/** Inspection choices that should seed estimate/contract material selections when cart is empty. */
export const INSPECTION_MATERIAL_PREFILL_RULES: InspectionMaterialPrefillRule[] = [
    {
        attributeName: "Underlayment Type",
        resolveCategory: resolveFieldDryInCategory,
        resolveProductValue: (_value, category) => category,
    },
    {
        attributeName: "Eave Drip Edge",
        category: "Drip Edge / Eave",
        resolveProductValue: (value) => normalizeDripEdgeSize(value),
    },
    {
        attributeName: "Rake Drip Edge",
        category: "Drip Edge / Rake",
        resolveProductValue: (value) => normalizeDripEdgeSize(value),
    },
];

function resolveFieldDryInCategory(value: string): string | null {
    const normalized = value.trim().toLowerCase();
    if (normalized === "synthetic") return "Field Dry In Synthetic";
    if (normalized === "felt 15 lb" || normalized === "15 lb felt" || normalized === "15# felt") {
        return "Field Dry In 15 Felt";
    }
    return null;
}

function normalizeDripEdgeSize(value: string): string | null {
    const normalized = value.trim().replace(/x/gi, "*");
    if (normalized === "2*2" || normalized === "2*4") return normalized;
    return null;
}

function categoryExists(materialSchema: any[], category: string): boolean {
    return materialSchema.some((item) => (item.Name || item.ProductName) === category);
}

function productExistsInCategory(materialSchema: any[], category: string, productValue: string): boolean {
    const schema = materialSchema.find((item) => (item.Name || item.ProductName) === category);
    if (!schema?.Subcategories?.length) return false;
    const key = productValue.trim().toLowerCase();
    return schema.Subcategories.some(
        (sub: any) =>
            (sub.Name || "").trim().toLowerCase() === key ||
            (sub.SystemLine || "").trim().toLowerCase() === key,
    );
}

function resolveGutterProduct(size: string): string | null {
    if (size === '5"' || size === '6"') return size;
    return null;
}

function resolveDownspoutProduct(size: string): string | null {
    if (size === '2x3"' || size === '3x4"') return size;
    return null;
}

function prefillMaterialSelection(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
    category: string,
    productValue: string,
): void {
    if (hydratedSelections[category]?.value) return;
    if (!categoryExists(materialSchema, category)) return;
    if (!productValue || !productExistsInCategory(materialSchema, category, productValue)) return;

    const sub = findSubcategory(materialSchema, category, productValue);
    const categorySchema = materialSchema.find((item) => (item.Name || item.ProductName) === category);
    const matchingSubs =
        categorySchema?.Subcategories?.filter(
            (s: any) => (s.Name || "").trim().toLowerCase() === productValue.trim().toLowerCase(),
        ) || [];
    const colorSubs = matchingSubs.filter((s: any) => s.Color);

    let awaitingColor = hasColorOptions(materialSchema, category, productValue);
    let manufacturerProductId = awaitingColor
        ? undefined
        : findManufacturerProductId(materialSchema, category, productValue);
    let color: string | undefined;

    if (awaitingColor && colorSubs.length === 1) {
        awaitingColor = false;
        manufacturerProductId = colorSubs[0].Id;
        color = colorSubs[0].Color;
    }

    hydratedSelections[category] = {
        value: productValue,
        systemLine: sub?.SystemLine || productValue,
        quantity: calculateQuantity(
            materialSchema,
            measurements,
            category,
            productValue,
            formulas,
            attributes,
            hydratedSelections,
        ),
        awaitingColor,
        ...(color ? { color } : {}),
        ...(manufacturerProductId ? { manufacturerProductId } : {}),
    };
}

const RIDGE_VENT_CATEGORY = "Ridge Vent";
const RIDGE_VENT_GENERIC_PRODUCT = "Ridge Vent (4ft)";
const BOX_ROOF_VENT_CATEGORY = "Box Roof Vent";
const BOX_ROOF_VENT_PRODUCT = "RV 51";
const TURBINE_ROOF_VENT_CATEGORY = "Turbine Roof Vent";
const TURBINE_ROOF_VENT_PRODUCT = '12" Turbine AtticVent';
const VALLEY_METAL_CATEGORY = "Valley metal";
const VALLEY_METAL_PRODUCT = "Open Valley metal";
const STEP_FLASHING_CATEGORY = "Step Flashing";
const STEP_FLASHING_PRODUCT = "4 X 4 Galv.";
const PLUMBING_JACKS_CATEGORY = "Plumbing Jacks";
const PLUMBING_JACKS_PRODUCT = "3 in 1 Ea";
const SPLITBOOT_CATEGORY = "Splitboot";
const SPLITBOOT_PRODUCT = "Generic Split Boot";
const BATH_VENT_CATEGORY = "Bath Vent";
const BATH_VENT_4IN_PRODUCT = "Generic 4inch";
const BATH_VENT_5IN_PRODUCT = "Generic 5inch";
const DRYER_VENT_CATEGORY = "Dryer Vent";
const DRYER_VENT_PRODUCT = "Braun Vent";
const OPEN_W_VALLEY_TYPES = new Set(["Open (W) Valley", "Open Metal"]);

function shouldPrefillValleyMetal(measurements: any[], attributes: any[]): boolean {
    const valleyType = getAttributeValue(attributes, "Valley Type");
    if (!valleyType || !OPEN_W_VALLEY_TYPES.has(valleyType)) return false;
    return getMeasurementLf(measurements, attributes, "Valley") > 0;
}

/** Prefill open W valley metal when inspection valley type is open and valley LF > 0. */
function applyValleyMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    if (!shouldPrefillValleyMetal(measurements, attributes)) return;

    prefillMaterialSelection(
        hydratedSelections,
        attributes,
        materialSchema,
        measurements,
        formulas,
        VALLEY_METAL_CATEGORY,
        VALLEY_METAL_PRODUCT,
    );
}

function shouldPrefillStepFlashing(measurements: any[], attributes: any[]): boolean {
    if (getAttributeQty(attributes, STEP_FLASHING_CATEGORY) > 0) return true;
    return getMeasurementLf(measurements, attributes, STEP_FLASHING_CATEGORY) > 0;
}

/** Prefill step flashing when inspection step flashing LF/count > 0. */
function applyStepFlashingMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    if (!shouldPrefillStepFlashing(measurements, attributes)) return;

    prefillMaterialSelection(
        hydratedSelections,
        attributes,
        materialSchema,
        measurements,
        formulas,
        STEP_FLASHING_CATEGORY,
        STEP_FLASHING_PRODUCT,
    );
}

function shouldPrefillPlumbingJacks(measurements: any[], attributes: any[]): boolean {
    if (getAttributeQty(attributes, PLUMBING_JACKS_CATEGORY) > 0) return true;
    return getMeasurementLf(measurements, attributes, PLUMBING_JACKS_CATEGORY) > 0;
}

/** Prefill plumbing jacks when inspection count > 0. */
function applyPlumbingJackMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    if (!shouldPrefillPlumbingJacks(measurements, attributes)) return;

    prefillMaterialSelection(
        hydratedSelections,
        attributes,
        materialSchema,
        measurements,
        formulas,
        PLUMBING_JACKS_CATEGORY,
        PLUMBING_JACKS_PRODUCT,
    );
}

function shouldPrefillSplitboot(attributes: any[]): boolean {
    return getAttributeQty(attributes, "Split Boots") > 0;
}

/** Prefill split boot when inspection count > 0. */
function applySplitbootMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    if (!shouldPrefillSplitboot(attributes)) return;

    prefillMaterialSelection(
        hydratedSelections,
        attributes,
        materialSchema,
        measurements,
        formulas,
        SPLITBOOT_CATEGORY,
        SPLITBOOT_PRODUCT,
    );
}

function resolveBathVentProduct(attributes: any[]): string | null {
    if (getAttributeQty(attributes, "Bathroom Vents") <= 0) return null;

    const size = (getAttributeValue(attributes, "Bath Vent Size") || "4 inch").trim();
    return size === "5 inch" ? BATH_VENT_5IN_PRODUCT : BATH_VENT_4IN_PRODUCT;
}

/** Prefill bath vent from inspection count + size (4 inch / 5 inch). */
function applyBathVentMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    const productValue = resolveBathVentProduct(attributes);
    if (!productValue) return;

    prefillMaterialSelection(
        hydratedSelections,
        attributes,
        materialSchema,
        measurements,
        formulas,
        BATH_VENT_CATEGORY,
        productValue,
    );
}

/** Prefill dryer vent when inspection count > 0. */
function applyDryerVentMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    if (getAttributeQty(attributes, "Dryer Vents") <= 0) return;

    prefillMaterialSelection(
        hydratedSelections,
        attributes,
        materialSchema,
        measurements,
        formulas,
        DRYER_VENT_CATEGORY,
        DRYER_VENT_PRODUCT,
    );
}

/** Prefill ridge / box / turbine roof vents from inspection ventilation counts. */
function applyRoofVentMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    const ventType = getAttributeValue(attributes, "Ventilation Type");

    if (ventType === RIDGE_VENT_VENTILATION_TYPE) {
        prefillMaterialSelection(
            hydratedSelections,
            attributes,
            materialSchema,
            measurements,
            formulas,
            RIDGE_VENT_CATEGORY,
            RIDGE_VENT_GENERIC_PRODUCT,
        );
        return;
    }

    if (!isRoofVentilationType(ventType)) return;

    if (getBoxVentCount(attributes) > 0) {
        prefillMaterialSelection(
            hydratedSelections,
            attributes,
            materialSchema,
            measurements,
            formulas,
            BOX_ROOF_VENT_CATEGORY,
            BOX_ROOF_VENT_PRODUCT,
        );
    }

    if (getTurbineVentCount(attributes) > 0) {
        prefillMaterialSelection(
            hydratedSelections,
            attributes,
            materialSchema,
            measurements,
            formulas,
            TURBINE_ROOF_VENT_CATEGORY,
            TURBINE_ROOF_VENT_PRODUCT,
        );
    }
}

/** Prefill Gutters / Downspouts product from inspection size when cart is empty. */
function applyGutterMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    const gutterSize = getAttributeValue(attributes, "Gutter Size") || "";
    const downspoutSize = getAttributeValue(attributes, "Downspout Size") || "";

    const gutterProduct = resolveGutterProduct(gutterSize);
    if (gutterProduct) {
        prefillMaterialSelection(
            hydratedSelections,
            attributes,
            materialSchema,
            measurements,
            formulas,
            "Gutters",
            gutterProduct,
        );
    }

    const downspoutProduct = resolveDownspoutProduct(downspoutSize);
    if (downspoutProduct) {
        prefillMaterialSelection(
            hydratedSelections,
            attributes,
            materialSchema,
            measurements,
            formulas,
            "Downspouts",
            downspoutProduct,
        );
    }

    const screenLength = Number(getAttributeValue(attributes, "Screen Length") || 0);
    if (screenLength > 0) {
        prefillMaterialSelection(
            hydratedSelections,
            attributes,
            materialSchema,
            measurements,
            formulas,
            "Gutter Screens",
            "General Gutter Screen",
        );
    }
}

/**
 * Seeds material selections from inspection attributes when the cart has no saved choice.
 * Cart / existing estimate selections always win.
 */
export function applyInspectionMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[] | undefined,
    materialSchema: any[],
    measurements: any[],
    formulas: any,
): void {
    if (!attributes?.length || !formulas) return;

    for (const rule of INSPECTION_MATERIAL_PREFILL_RULES) {
        const raw = getAttributeValue(attributes, rule.attributeName);
        if (!raw) continue;

        const maybeCategory = rule.resolveCategory?.(raw) ?? rule.category;
        if (maybeCategory == null || maybeCategory === "") continue;

        const productValue = rule.resolveProductValue
            ? rule.resolveProductValue(raw, maybeCategory)
            : raw.trim();
        if (!productValue) continue;
        prefillMaterialSelection(
            hydratedSelections,
            attributes,
            materialSchema,
            measurements,
            formulas,
            maybeCategory,
            productValue,
        );
    }

    applyGutterMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applyValleyMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applyStepFlashingMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applyPlumbingJackMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applySplitbootMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applyBathVentMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applyDryerVentMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applyRoofVentMaterialPrefills(hydratedSelections, attributes, materialSchema, measurements, formulas);
    applyFurnaceMaterialPrefills(hydratedSelections, attributes, materialSchema);
}

function applyFurnaceMaterialPrefills(
    hydratedSelections: Record<string, any>,
    attributes: any[] | undefined,
    materialSchema: any[],
): void {
    for (const category of MULTI_LINE_MATERIAL_CATEGORIES) {
        if (isMultiLineSelection(hydratedSelections[category]) && hydratedSelections[category].lines.length > 0) {
            continue;
        }
        if (!categoryExists(materialSchema, category)) continue;

        const lines = buildLinesFromFurnaceInspection(materialSchema, category, attributes);
        if (lines.length === 0) continue;

        hydratedSelections[category] = { multiLine: true, lines };
    }
}
